import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  useColorMode,
  SimpleGrid,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getBrandValue, getColor } from "../brandConfig";
import { useEffect } from "react";

const AboutUs = () => {
  const { colorMode } = useColorMode();

  // Handle scroll to team section on page load
  useEffect(() => {
    const shouldScrollToTeam = sessionStorage.getItem("scrollToTeamSection");

    // Reset body opacity for smooth fade in
    const body = document.body;
    body.style.opacity = '1';
    body.style.transition = 'opacity 0.15s ease-in';

    if (shouldScrollToTeam === "true") {
      sessionStorage.removeItem("scrollToTeamSection");

      const scrollTimer = setTimeout(() => {
        const teamSection = document.getElementById("team-section");

        if (teamSection) {
          const rect = teamSection.getBoundingClientRect();
          const scrollTop = window.pageYOffset + rect.top;

          window.scrollTo({
            top: scrollTop,
            left: 0,
            behavior: 'auto'
          });
        }
      }, 200);

      return () => clearTimeout(scrollTimer);
    }

  }, []);

  return (
    <Box
      bg="#EBEBEB"
      minHeight="100vh"
      position="relative"
      overflowX="hidden"
      display="flex"
      flexDirection="column"
      sx={{
        "@keyframes fadeInUp": {
          "0%": {
            opacity: 0,
            transform: "translateY(30px)"
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)"
          }
        },
        "@keyframes fadeInUpStagger": {
          "0%": {
            opacity: 0,
            transform: "translateY(20px)"
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)"
          }
        }
      }}
    >
      <NavbarWithCallToAction />

      {/* Scrolling Content Container */}
      <Box position="relative" zIndex={1}>

        {/* Page Header */}
        <Box
          bg="#EBEBEB"
          position="relative"
          pt={{ base: "150px", md: "200px", lg: "260px" }}
          pb={{ base: "40px", md: "50px", lg: "60px" }}
          textAlign="center"
        >
          <Container maxW="1400px" px={{ base: 6, md: 8 }}>
            <VStack spacing={8}>
              <Box
                position="relative"
                opacity={0}
                transform="translateY(30px)"
                animation="fadeInUp 1.0s ease-out 0.2s forwards"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '3px',
                  bg: '#FF6600',
                  borderRadius: 'full'
                }}
              >
                <Heading
                  as="h1"
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="700"
                  color="#03177A"
                  fontFamily={getBrandValue("fonts.heading")}
                  letterSpacing="0.05em"
                  mb={4}
                >
                  About Us
                </Heading>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* Who We Are Section */}
        <Box
          bg="#EBEBEB"
          position="relative"
          py={{ base: 10, md: 16, lg: 20 }}
        >
          <Container maxW="1200px" px={{ base: 6, md: 12, lg: 16 }}>
            <Box
              position="relative"
              p={{ base: 8, md: 10, lg: 12 }}
              bg="rgba(255, 255, 255, 0.8)"
              borderRadius="2xl"
              border="1px solid rgba(3, 23, 122, 0.1)"
              boxShadow="0 4px 20px rgba(3, 23, 122, 0.08)"
              opacity={0}
              transform="translateY(40px)"
              animation="fadeInUp 1.0s ease-out 0.5s forwards"
              _hover={{
                boxShadow: "0 8px 30px rgba(3, 23, 122, 0.12)",
                transform: "translateY(-4px)",
              }}
              transition="all 0.3s ease"
            >
              <VStack spacing={8} align="start" w="100%">
                <HStack spacing={4} align="center">
                  <Box
                    width="4px"
                    height="40px"
                    bg="#FF6600"
                    borderRadius="full"
                  />
                  <Heading
                    as="h2"
                    fontSize={{ base: "2xl", md: "3xl" }}
                    fontWeight="700"
                    color="#03177A"
                    fontFamily={getBrandValue("fonts.heading")}
                  >
                    Who We Are
                  </Heading>
                </HStack>
                <VStack spacing={5} align="center" w="100%" textAlign="center">
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="rgba(3, 23, 122, 0.8)"
                    lineHeight="1.8"
                    fontFamily={getBrandValue("fonts.body")}
                    maxW="900px"
                  >
                    Vanguard Care delivers innovative residential aged care in Queensland. We work collaboratively with residents, their families, and our dedicated team to create supportive and welcoming environments.
                  </Text>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="rgba(3, 23, 122, 0.8)"
                    lineHeight="1.8"
                    fontFamily={getBrandValue("fonts.body")}
                    maxW="900px"
                  >
                    Our commitment is to provide person-centred care that respects the dignity, choices, and individual needs of every resident. We believe that quality aged care should feel like home.
                  </Text>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="rgba(3, 23, 122, 0.8)"
                    lineHeight="1.8"
                    fontFamily={getBrandValue("fonts.body")}
                    maxW="900px"
                  >
                    By combining clinical excellence with genuine compassion, Vanguard Care creates communities where residents can thrive, families can feel at ease, and staff are proud to work.
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </Container>
        </Box>

        {/* Why Vanguard Care Section */}
        <Box
          bg="#03177A"
          position="relative"
          py={{ base: 16, md: 20, lg: 24 }}
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            opacity={0.03}
            backgroundImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
            backgroundSize="50px 50px"
          />
          <Container maxW="1400px" px={{ base: 6, md: 8, lg: 12 }} position="relative" zIndex={1}>
            <VStack spacing={{ base: 12, md: 16 }} textAlign="center">
              <Box
                position="relative"
                opacity={0}
                transform="translateY(30px)"
                animation="fadeInUp 0.8s ease-out 0.8s forwards"
              >
                <Heading
                  as="h2"
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="700"
                  color="#FFFFFF"
                  fontFamily={getBrandValue("fonts.heading")}
                  letterSpacing="0.05em"
                  mb={4}
                >
                  Why Vanguard Care
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="rgba(255, 255, 255, 0.7)"
                  maxW="700px"
                  mx="auto"
                  lineHeight="1.7"
                  fontFamily={getBrandValue("fonts.body")}
                >
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla
                  volutpat euismod vehicula. Suspendisse potenti. Cras pretium
                  viverra eros, nec ultricies ex eleifend ut.
                </Text>
              </Box>

              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={{ base: 6, md: 8 }}
                opacity={0}
                transform="translateY(30px)"
                animation="fadeInUpStagger 0.8s ease-out 1.2s forwards"
              >
                {/* Communities that flourish */}
                <Box
                  bg="#FFFFFF"
                  borderRadius="xl"
                  p={{ base: 6, md: 8 }}
                  textAlign="left"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <Image
                    src="/icons/VC_Icons_Flourish.png"
                    alt="Communities that flourish"
                    w={{ base: "48px", md: "56px" }}
                    h={{ base: "48px", md: "56px" }}
                    mb={5}
                  />
                  <Heading
                    as="h4"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="700"
                    color="#03177A"
                    fontFamily={getBrandValue("fonts.heading")}
                    mb={3}
                  >
                    Communities that flourish
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(3, 23, 122, 0.7)"
                    lineHeight="1.8"
                    fontFamily={getBrandValue("fonts.body")}
                  >
                    Vanguard Care is dedicated to creating environments where individuals can flourish. We foster community connections and deliver personalised care to help you stay safe, independent, active and connected.
                  </Text>
                </Box>

                {/* A compassionate & skilled team */}
                <Box
                  bg="#FFFFFF"
                  borderRadius="xl"
                  p={{ base: 6, md: 8 }}
                  textAlign="left"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <Image
                    src="/icons/VC_Icons_Team.png"
                    alt="A compassionate & skilled team"
                    w={{ base: "48px", md: "56px" }}
                    h={{ base: "48px", md: "56px" }}
                    mb={5}
                  />
                  <Heading
                    as="h4"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="700"
                    color="#03177A"
                    fontFamily={getBrandValue("fonts.heading")}
                    mb={3}
                  >
                    A compassionate & skilled team
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(3, 23, 122, 0.7)"
                    lineHeight="1.8"
                    fontFamily={getBrandValue("fonts.body")}
                  >
                    Our dedicated, compassionate team is here to support you with understanding and respect. They're trained to listen, advocate, and provide tailored care that helps you maintain your dignity and independence.
                  </Text>
                </Box>

                {/* Collaborative care */}
                <Box
                  bg="#FFFFFF"
                  borderRadius="xl"
                  p={{ base: 6, md: 8 }}
                  textAlign="left"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <Image
                    src="/icons/VC_Icons_Care.png"
                    alt="Collaborative care"
                    w={{ base: "48px", md: "56px" }}
                    h={{ base: "48px", md: "56px" }}
                    mb={5}
                  />
                  <Heading
                    as="h4"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="700"
                    color="#03177A"
                    fontFamily={getBrandValue("fonts.heading")}
                    mb={3}
                  >
                    Collaborative care
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(3, 23, 122, 0.7)"
                    lineHeight="1.8"
                    fontFamily={getBrandValue("fonts.body")}
                  >
                    We believe that the best care comes from working in partnership with you, your family, your dedicated care team and partners. Together, we build a supportive community, ensuring you have the personalised support and connections you need to thrive.
                  </Text>
                </Box>

                {/* Innovative tech-enabled care */}
                <Box
                  bg="#FFFFFF"
                  borderRadius="xl"
                  p={{ base: 6, md: 8 }}
                  textAlign="left"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <Image
                    src="/icons/VC_Icons_Innovation.png"
                    alt="Innovative tech-enabled care"
                    w={{ base: "48px", md: "56px" }}
                    h={{ base: "48px", md: "56px" }}
                    mb={5}
                  />
                  <Heading
                    as="h4"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="700"
                    color="#03177A"
                    fontFamily={getBrandValue("fonts.heading")}
                    mb={3}
                  >
                    Innovative tech-enabled care
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="rgba(3, 23, 122, 0.7)"
                    lineHeight="1.8"
                    fontFamily={getBrandValue("fonts.body")}
                  >
                    Vanguard Care is dedicated to creating environments where individuals can flourish. We foster community connections and deliver personalised care to help you stay safe, independent, active and connected.
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Meet the Team Section */}
        <Box
          id="team-section"
          bg="#EBEBEB"
          position="relative"
          py={{ base: 16, md: 20, lg: 24 }}
        >
          <Container maxW="1200px" px={{ base: 6, md: 8 }}>
            <VStack spacing={{ base: 10, md: 14 }}>
              <Box
                position="relative"
                textAlign="center"
                opacity={0}
                transform="translateY(30px)"
                animation="fadeInUp 0.8s ease-out 0.3s forwards"
              >
                <Heading
                  as="h2"
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="700"
                  color="#03177A"
                  fontFamily={getBrandValue("fonts.heading")}
                  letterSpacing="0.05em"
                  mb={4}
                >
                  Meet the Team
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="rgba(3, 23, 122, 0.7)"
                  maxW="600px"
                  mx="auto"
                  lineHeight="1.7"
                  fontFamily={getBrandValue("fonts.body")}
                >
                  The passionate people behind Vanguard Care, dedicated to
                  making a difference every day.
                </Text>
              </Box>

              <SimpleGrid
                columns={{ base: 1, sm: 2, lg: 4 }}
                spacing={{ base: 6, md: 8 }}
                w="100%"
                opacity={0}
                transform="translateY(30px)"
                animation="fadeInUpStagger 0.8s ease-out 0.6s forwards"
              >
                {[
                  {
                    name: "Dr. Sarah Chen",
                    role: "Chief Executive Officer",
                    bio: "With over 20 years of experience in aged care leadership, Sarah is passionate about creating innovative care models that put residents first.",
                  },
                  {
                    name: "James Mitchell",
                    role: "Director of Clinical Services",
                    bio: "A registered nurse with extensive experience in geriatric care, James leads our clinical teams to deliver the highest standards of care.",
                  },
                  {
                    name: "Emily Nguyen",
                    role: "Head of Operations",
                    bio: "Emily brings a wealth of operational expertise, ensuring our facilities run smoothly and our teams have everything they need to provide exceptional care.",
                  },
                  {
                    name: "David Thompson",
                    role: "Director of Education & Training",
                    bio: "David is dedicated to workforce development, designing training programs that equip our staff with the skills and compassion to excel.",
                  },
                ].map((member) => (
                  <VStack
                    key={member.name}
                    spacing={4}
                    textAlign="center"
                    transition="all 0.3s ease"
                    _hover={{ transform: "translateY(-4px)" }}
                  >
                    <Box
                      w={{ base: "160px", md: "180px" }}
                      h={{ base: "160px", md: "180px" }}
                      borderRadius="full"
                      overflow="hidden"
                      bg="linear-gradient(135deg, #E6EEFF 0%, #d5d5d5 100%)"
                      border="3px solid rgba(3, 23, 122, 0.1)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="3xl">👤</Text>
                    </Box>

                    <VStack spacing={1}>
                      <Heading
                        as="h4"
                        fontSize={{ base: "md", md: "lg" }}
                        fontWeight="700"
                        color="#03177A"
                        fontFamily={getBrandValue("fonts.heading")}
                      >
                        {member.name}
                      </Heading>
                      <Text
                        fontSize="sm"
                        fontWeight="600"
                        color="#FF6600"
                        fontFamily={getBrandValue("fonts.body")}
                      >
                        {member.role}
                      </Text>
                    </VStack>

                    <Text
                      fontSize="sm"
                      color="rgba(3, 23, 122, 0.65)"
                      lineHeight="1.7"
                      fontFamily={getBrandValue("fonts.body")}
                    >
                      {member.bio}
                    </Text>
                  </VStack>
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Bottom spacing before footer */}
        <Box bg="#EBEBEB" py={{ base: 4, md: 6 }} />

      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default AboutUs;
